/**
 * Centralized Deduplicated Toast Manager
 * 
 * Prevents toast flooding by:
 * 1. Deduplicating toasts with the same title within a cooldown window
 * 2. Grouping concurrent server/network errors into a single toast
 * 3. Limiting max visible toasts to 2
 */

// Track recently shown toasts with timestamps
const recentToasts = new Map();

// Cooldown period in ms (same error won't show again within this window)
const COOLDOWN_MS = 6000;

// Max visible toasts at once
const MAX_VISIBLE = 2;

// Track active toast count
let activeToastCount = 0;

/**
 * Generate a deduplication key from toast options
 */
const getDedupeKey = (options) => {
  return `${options.title || ''}::${options.status || 'info'}`;
};

/**
 * Check if a toast with this key was recently shown
 */
const isRecentlyShown = (key) => {
  const lastShown = recentToasts.get(key);
  if (!lastShown) return false;
  return (Date.now() - lastShown) < COOLDOWN_MS;
};

/**
 * Create a deduplicated toast function that wraps Chakra's toast
 * @param {Function} chakraToast - The raw Chakra useToast() instance
 * @returns {Function} Wrapped toast function with deduplication
 */
export const createDedupedToast = (chakraToast) => {
  return (options) => {
    const key = getDedupeKey(options);

    // Skip if this exact toast was shown recently
    if (isRecentlyShown(key)) {
      return;
    }

    // Skip if we have too many active toasts
    if (activeToastCount >= MAX_VISIBLE) {
      return;
    }

    // Record this toast
    recentToasts.set(key, Date.now());
    activeToastCount++;

    // Auto-decrement active count when toast duration expires
    const duration = options.duration || 5000;
    setTimeout(() => {
      activeToastCount = Math.max(0, activeToastCount - 1);
    }, duration);

    // Clean up old entries periodically
    if (recentToasts.size > 20) {
      const now = Date.now();
      for (const [k, timestamp] of recentToasts) {
        if (now - timestamp > COOLDOWN_MS * 2) {
          recentToasts.delete(k);
        }
      }
    }

    chakraToast({
      position: 'top',
      variant: 'subtle',
      isClosable: true,
      ...options,
    });
  };
};

/**
 * Create a deduplicated toast specifically for the API interceptor.
 * Groups concurrent server errors (500/502/503) into a single connectivity toast.
 */
let serverErrorTimeout = null;
let pendingServerErrorCount = 0;

export const createInterceptorToast = (chakraToast) => {
  const dedupedToast = createDedupedToast(chakraToast);

  return (options) => {
    const title = options.title || '';

    // Group server errors (500, 502, 503) and network errors together
    const isServerError = ['Server Error', 'Network Error', 'Unexpected Error'].includes(title);

    if (isServerError) {
      pendingServerErrorCount++;

      // If already scheduled, skip — we'll show a grouped message
      if (serverErrorTimeout) return;

      // Batch concurrent server errors into 1 toast after 300ms
      serverErrorTimeout = setTimeout(() => {
        const count = pendingServerErrorCount;
        pendingServerErrorCount = 0;
        serverErrorTimeout = null;

        dedupedToast({
          title: 'Connection Issue',
          description: count > 1
            ? 'Some features may be temporarily unavailable. Retrying automatically...'
            : options.description || 'Unable to reach the server. Please try again.',
          status: 'warning',
          duration: 6000,
          isClosable: true,
        });
      }, 300);

      return;
    }

    // Non-server errors pass through normally (still deduplicated)
    dedupedToast(options);
  };
};

/**
 * Reset toast state (useful for testing)
 */
export const resetToastState = () => {
  recentToasts.clear();
  activeToastCount = 0;
  pendingServerErrorCount = 0;
  if (serverErrorTimeout) {
    clearTimeout(serverErrorTimeout);
    serverErrorTimeout = null;
  }
};
